import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useOrg } from "../lib/org";
import {
  Button,
  ErrorState,
  Field,
  Modal,
  Money,
  PageHeader,
  Spinner,
  TextField,
} from "../components/ui";
import { DataTable } from "../components/DataTable";
import type { Vendor } from "../lib/models";
import { EditVendor } from './EditVendor';

export default function Vendors() {
  const { currentOrg } = useOrg();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<{vendor:Vendor;orgId:string}|null>(null);
  const capability = useQuery({queryKey:['vendor-editing',currentOrg?.id],queryFn:()=>api.get<{enabled:boolean}>('/vendor-editing/capability'),enabled:!!currentOrg,retry:false});
  const edit = (vendor:Vendor) => { if(currentOrg)setEditing({vendor,orgId:currentOrg.id}); };
  const currency = currentOrg?.baseCurrency ?? "USD";

  const { data, isLoading, error } = useQuery({
    queryKey: ["vendors", currentOrg?.id],
    queryFn: () => api.get<Vendor[]>("/vendors"),
    enabled: !!currentOrg,
  });

  return (
    <div>
      <PageHeader
        title="Vendors"
        subtitle="Suppliers and payees you owe"
        action={<Button onClick={() => setOpen(true)}>New vendor</Button>}
      />
      {isLoading && <Spinner />}
      {error && <ErrorState error={error} />}
      {data && (
        <DataTable
          rows={data}
          rowKey={(r) => r.id}
          columns={[
            {
              key: "name",
              header: "Name",
              render: (r) => (
                <div>
                  {capability.data?.enabled?<button type="button" className="font-medium text-brand-700 underline" onClick={()=>edit(r)}>{r.displayName}</button>:<div className="font-medium text-gray-900">{r.displayName}</div>}
                  {r.companyName && (
                    <div className="text-xs text-gray-400">{r.companyName}</div>
                  )}
                </div>
              ),
            },
            { key: "email", header: "Email", render: (r) => r.email ?? "—" },
            { key: "phone", header: "Phone", render: (r) => r.phone ?? "—" },
            ...(capability.data?.enabled?[{key:'edit',header:'Actions',render:(r:Vendor)=><Button type="button" variant="secondary" onClick={()=>edit(r)}>Edit</Button>}]:[]),
            {
              key: "balance",
              header: "Open balance",
              align: "right",
              render: (r) => <Money cents={r.balance} currency={currency} />,
            },
          ]}
        />
      )}

      {editing&&editing.orgId===currentOrg?.id&&capability.data?.enabled&&<EditVendor key={editing.orgId+editing.vendor.id} vendor={editing.vendor} orgId={editing.orgId} onClose={()=>setEditing(null)}/>}
      <NewVendorModal
        open={open}
        onClose={() => setOpen(false)}
        onCreated={() => {
          setOpen(false);
          qc.invalidateQueries({ queryKey: ["vendors"] });
        }}
      />
    </div>
  );
}

function NewVendorModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState({
    displayName: "",
    companyName: "",
    email: "",
    phone: "",
    notes: "",
  });
  const create = useMutation({
    mutationFn: () =>
      api.post("/vendors", {
        displayName: form.displayName,
        companyName: form.companyName || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        notes: form.notes || undefined,
      }),
    onSuccess: onCreated,
  });

  return (
    <Modal open={open} onClose={onClose} title="New vendor">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <Field label="Display name">
          <TextField
            required
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Company">
            <TextField
              value={form.companyName}
              onChange={(e) => setForm({ ...form, companyName: e.target.value })}
            />
          </Field>
          <Field label="Email">
            <TextField
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Phone">
          <TextField
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </Field>
        {create.error && <ErrorState error={create.error} />}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Saving…" : "Create vendor"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
